import { useCallback, useMemo, useRef } from 'react';

import type {
  ComposerProductInput,
  ComposerScene
} from '@vellnes/composer-react';

import {
  buildSceneId,
  loadComposerSceneEntry,
  saveComposerSceneEntry,
  PersistedComposerRenderGeneration,
  PersistedComposerDisplaySettings
} from '../../utils/composerSceneStorage';

interface UseComposerScenePersistenceOptions {
  products: ComposerProductInput[];
}

// Persists both the composer scene layout and its render-generation history
// in localStorage, keyed by the sorted set of product nanoIds — so reopening
// Compose with the exact same product selection restores the previously
// arranged scene and lets the user see past render results, instead of
// starting from the widget's default auto-layout with an empty sidebar.
function useComposerScenePersistence({
  products
}: UseComposerScenePersistenceOptions) {
  const sceneId = useMemo(
    () => buildSceneId(products.map((product) => product.id)),
    [products]
  );

  const storedEntry = useMemo(() => loadComposerSceneEntry(sceneId), [sceneId]);

  // The composer widget owns the live scene state; render generations and
  // display settings live in separate hooks. All three need to land in the
  // same localStorage entry, so the latest values are cached here and
  // merged in whichever callback fires last.
  const latestSceneRef = useRef<ComposerScene | undefined>(storedEntry?.scene);
  const latestRenderGenerationsRef = useRef<
    PersistedComposerRenderGeneration[]
  >(storedEntry?.renderGenerations ?? []);
  const latestDisplaySettingsRef = useRef<
    PersistedComposerDisplaySettings | undefined
  >(storedEntry?.displaySettings);

  const persist = useCallback(() => {
    if (!latestSceneRef.current) return;

    saveComposerSceneEntry(sceneId, {
      scene: latestSceneRef.current,
      renderGenerations: latestRenderGenerationsRef.current,
      ...(latestDisplaySettingsRef.current && {
        displaySettings: latestDisplaySettingsRef.current
      })
    });
  }, [sceneId]);

  const handleSceneChange = useCallback(
    (scene: ComposerScene) => {
      latestSceneRef.current = scene;
      persist();
    },
    [persist]
  );

  const handlePersistRenderGenerations = useCallback(
    (renderGenerations: PersistedComposerRenderGeneration[]) => {
      latestRenderGenerationsRef.current = renderGenerations;
      persist();
    },
    [persist]
  );

  const handlePersistDisplaySettings = useCallback(
    (displaySettings: PersistedComposerDisplaySettings) => {
      latestDisplaySettingsRef.current = displaySettings;
      persist();
    },
    [persist]
  );

  return {
    sceneId,
    initialScene: storedEntry?.scene,
    initialRenderGenerations: storedEntry?.renderGenerations ?? [],
    initialDisplaySettings: storedEntry?.displaySettings,
    handleSceneChange,
    handlePersistRenderGenerations,
    handlePersistDisplaySettings
  };
}

export default useComposerScenePersistence;
